import React from 'react';
import { Link } from 'react-router-dom';
import { useDesignTemplate } from '../../contexts/DesignTemplateContext';
import { getLogoUrl, getLogoStyle } from '../../utils/logoHelper';
import gamesService from '../../services/games';
import { getProviderNames } from '../../utils/providerUtils';
import './Footer.css';

/**
 * 루미나 푸터 — 블랙 `Footer.jsx` 의 화면 층 신작(데이터 규칙은 원본 그대로).
 *   로고 → SNS 목록(관리자 '디자인 토큰 편집 > SNS 목록 (푸터)', 없으면 푸터 텔레그램 설정) → 카테고리 글자(게임사 나열, '카테고리 글자 · 푸터 문구') → 저작권.
 *   블랙의 벤더 로고 슬라이더(VendorLogoSlider)는 루미나에서 게임 실행 화면의 게임사 띠로 옮겨져 푸터에서는 그리지 않는다.
 *   [2026-10-31] 게임사 나열은 관리자 문구가 없으면 이 사이트가 실제로 노출 중인 게임사 이름으로 채운다(아래 주석) — 종전의 코드에 박힌
 *   13개 이름은 지웠다.
 */

const TelegramIcon = () => (
  <svg viewBox="0 0 24 24"><path d="M21.5 3.5 2.8 10.7c-1 .4-1 1 0 1.3l4.8 1.5 1.8 5.6c.2.6.4.8.9.8.4 0 .6-.2.9-.5l2.6-2.5 5 3.7c.9.5 1.6.2 1.8-.9L23 5c.3-1.3-.5-1.9-1.5-1.5Z"/></svg>
);

const Footer = () => {
  const designTemplate = useDesignTemplate();
  const [logoBroken, setLogoBroken] = React.useState(false);   // 로고 이미지 404(미등록 주소·직접 포트 접근) 시 글자 마크로

  const siteName =
    designTemplate?.theme?.site?.title ||
    designTemplate?.user_browser_title ||
    designTemplate?.site_title ||
    'NEWMOON';

  let dtFooter = designTemplate?.design_tokens;
  if (typeof dtFooter === 'string') {
    try { dtFooter = JSON.parse(dtFooter); } catch (e) { dtFooter = {}; }
  }
  const footerTg = (dtFooter && typeof dtFooter.footer_telegram === 'object') ? dtFooter.footer_telegram : {};
  const 푸터문구 = (dtFooter && typeof dtFooter.footer_text === 'object') ? dtFooter.footer_text : {};
  const 관리자_게임사나열 = (typeof 푸터문구.vendors === 'string' && 푸터문구.vendors.trim()) ? 푸터문구.vendors.trim() : '';
  /* [2026-10-31] 관리자 '푸터 · 게임사 나열'이 비어 있으면, 회원이 "카지노"·"슬롯" 화면에서 보는 것과 같은 게임사 목록(같은 요청)의
     이름을 카지노 → 슬롯 순으로, 같은 이름은 한 번만 이어 쓴다. 이름 규칙은 게임사 카드와 같다(name_ko → providerUtils 한글명).
     WHY: 종전 기본값은 코드에 박힌 13개 이름이라 이 사이트에 없는 게임사(올벳·WM카지노·드래곤소프트)가 보였다(2026-10-31 ur3 실측,
          디자인설정 규칙 W09 '지어낸 값 표시 금지'). 목록을 못 받으면 지어낸 값 대신 줄을 그리지 않는다. */
  const [노출_게임사나열, set노출_게임사나열] = React.useState('');
  React.useEffect(() => {
    if (관리자_게임사나열) return undefined;
    let alive = true;
    Promise.all([gamesService.getProviders('casino'), gamesService.getProviders('slot')])
      .then((results) => {
        if (!alive) return;
        const names = [];
        results.forEach((data) => {
          (data?.providers || []).forEach((p) => {
            const code = (p.provider || p.code || p.provider_name || p.name || '').toString();
            const name = p.name_ko || (code ? getProviderNames(code).korean : '');
            if (name && !names.includes(name)) names.push(name);
          });
        });
        // 구분 기호 앞 칸은 줄바꿈 없는 칸(\u00a0) — 줄이 '|' 로 시작하지 않고 앞 이름 뒤 '|' 에서 끝나게 한다(2026-10-31 360폭 실측: 줄 머리에 '|')
        set노출_게임사나열(names.join('\u00a0| '));
      })
      .catch(() => { if (alive) set노출_게임사나열(''); });
    return () => { alive = false; };
  }, [관리자_게임사나열]);
  const 게임사나열 = 관리자_게임사나열 || 노출_게임사나열;
  const 저작권문구 = (typeof 푸터문구.copyright === 'string' && 푸터문구.copyright.trim()) ? 푸터문구.copyright.trim() : null;
  const snsLinks = (() => {
    const arr = dtFooter?.sns_links;
    if (!Array.isArray(arr)) return [];
    return arr.filter((x) => x && x.enabled !== false && (x.account || x.url || x.name));
  })();
  const qlTelegram = Array.isArray(dtFooter?.quicklinks) ? dtFooter.quicklinks[0] : null;
  const footerTgUrl =
    (typeof footerTg.url === 'string' && footerTg.url.trim()) ? footerTg.url.trim()
    : (qlTelegram && typeof qlTelegram.url === 'string' && qlTelegram.url.trim()) ? qlTelegram.url.trim()
    : '';
  const showFooterTelegram = footerTg.enabled === true && !!footerTgUrl;

  return (
    <footer>
      <div className="container">
        <div className="foot-top">
          <Link to="/" className="logo" aria-label={siteName}>
            {getLogoUrl(designTemplate, 'footer') && !logoBroken
              ? <img src={getLogoUrl(designTemplate, 'footer')} alt={siteName} style={getLogoStyle(designTemplate, 'footer')} className="logo-img" loading="lazy" onError={() => setLogoBroken(true)} />
              : <><span className="mk">{(siteName || 'NM').slice(0, 2).toUpperCase()}</span><span className="wd">{siteName}</span></>}
          </Link>
          {/* SNS 목록(관리자 지정) — 한 항목도 없으면 종전 텔레그램 표시(무손상) */}
          <div className="foot-sns">
            {snsLinks.length > 0 && snsLinks.map((s, i) => {
              const 내용 = (
                <>
                  {s.icon ? <img src={s.icon} alt={s.name || ''} style={{ width: 16, height: 16, objectFit: 'contain' }} /> : <TelegramIcon />}
                  <span>{s.account || s.name}</span>
                </>
              );
              return s.url
                ? <a key={i} href={s.url} target="_blank" rel="noopener noreferrer" className="foot-sns-link">{내용}</a>
                : <span key={i} className="foot-sns-link">{내용}</span>;
            })}
            {snsLinks.length === 0 && showFooterTelegram && (
              <a href={footerTgUrl} target="_blank" rel="noopener noreferrer" className="foot-sns-link"><TelegramIcon />텔레그램</a>
            )}
          </div>
        </div>
        {/* 카테고리 글자(게임사 나열) — 관리자 지정이 있으면 그 문구, 없으면 실제 노출 게임사(위 주석). 둘 다 없으면 줄을 그리지 않는다 */}
        {게임사나열 && <p className="foot-cats">{게임사나열}</p>}
        <div className="foot-bottom">
          <div className="age">18+</div>
          <p className="legal">{저작권문구 || <>Copyright &copy; {siteName} All Rights Reserved.</>}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
